import { memo } from 'react';
import classnames from 'classnames';
import styles from './Text.module.scss';
import { typography } from './typography';

export type Variants = keyof typeof typography;
export type FontTypes = 'montserrat' | 'openSans';
export type TextAlignTypes =
    | 'center'
    | 'start'
    | 'end'
    | 'justify'
    | 'right'
    | 'left';
export type ColorsTypes =
    | 'primary'
    | 'primaryDark'
    | 'secondary'
    | 'secondaryDark'
    | 'promo50'
    | 'promo40'
    | 'promo30'
    | 'promo20'
    | 'promo10'
    | 'black'
    | 'dark'
    | 'dark2'
    | 'grey'
    | 'grey2'
    | 'light'
    | 'lightBorder'
    | 'lightBg'
    | 'white'
    | 'negative'
    | 'positive'
    | 'information'
    | 'background'
    | 'resourcesHeader'
    | 'diviserBorder'
    | 'timeTableCell'
    | 'systemGrey'
    | 'borderField'
    | 'commentBackground'
    | 'commentBorder'
    | 'icon1'
    | 'icon2'
    | 'weekCalendar'
    | 'error'
    | 'error2'
    | 'divider'
    | 'timelinerGrey'
    | 'disabledChipBackground'
    | 'disabledChip'
    | 'placeholder';

export type CssParams = {
    font?: FontTypes;
    color?: ColorsTypes;
    align?: TextAlignTypes;
    bold?: boolean;
    italic?: boolean;
    elipsis?: boolean;
    capitalize?: boolean;
    fontSize?: number;
    lineHeight?: number;
    height?: number;
    noWrap?: boolean;
};

export type TextProps = {
    children?: React.ReactNode;
    className?: string;
    variant?: Variants;
    'data-cy'?: string;
} & CssParams;

const defaultsParams: CssParams = {
    elipsis: false,
    font: 'montserrat',
    color: undefined,
    bold: false,
    italic: false,
    fontSize: 15,
    lineHeight: undefined,
    align: undefined,
    capitalize: false,
    height: undefined,
    noWrap: false,
};

const Text = ({
    children,
    className,
    variant,
    'data-cy': dataCy,
    ...rest
}: TextProps) => {
    const params = {
        ...defaultsParams,
        ...(variant ? typography[variant] : {}),
        ...rest,
    };
    const cssStyles = {
        ...(params.fontSize ? { fontSize: `${params.fontSize}px` } : {}),
        ...(params.lineHeight ? { lineHeight: `${params.lineHeight}px` } : {}),
        ...(params.height ? { height: `${params.height}px` } : {}),
    };
    const finalClassnames = classnames(
        {
            [styles[`color-${params.color || 'primary'}`]]: params.color,
            [styles[`textAlign-${params.align || 'start'}`]]: params.align,
            [styles[params?.font || 'montserrat']]: params.font,
            [styles.elipsis]: params.elipsis,
            [styles.semibold]: params.bold,
            [styles.italic]: params.italic,
            [styles.capitalize]: params.capitalize,
            [styles.noWrap]: params.noWrap,
        },
        className,
    );
    return (
        <span className={finalClassnames} style={cssStyles} data-cy={dataCy}>
            {children}
        </span>
    );
};

export default memo(Text);
